import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { apiClient } from '../../api'
import { UploadedAsset } from '../../types/chatTypes'
import { validateFile } from '../../utils/documentUtils'

export function useDocumentUpload() {
  const { t } = useTranslation()
  const [uploadedDocument, setUploadedDocument] = useState<UploadedAsset | undefined>(undefined)
  const [isUploading, setIsUploading] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [lastError, setLastError] = useState<string | null>(null)

  const handleFileUpload = useCallback(async (file: File) => {
    // Reset previous errors
    setLastError(null);

    // Validate file before upload
    const validationError = validateFile(file)
    if (validationError) {
      // Callers may ignore the returned error, so surface it directly.
      const message = t(`chat.${validationError}`);
      setLastError(message);
      toast.error(message);
      return { success: false, error: message };
    }

    setIsUploading(true)
    try {
      const response = await apiClient.document.uploadDocument(file)
      if (response.success) {
        setUploadedDocument(response.document)
        toast.success(String(t('chat.documentUploaded')))
        return { success: true };
      }
      const message = t('chat.documentUploadFailed');
      setLastError(message);
      toast.error(message);
      return { success: false, error: message };
    } catch (error: any) {
      const errorMessage = error?.message || t('chat.documentUploadFailed');
      setLastError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsUploading(false)
    }
  }, [t])

  const handleDocumentDelete = useCallback(async () => {
    if (!uploadedDocument) {
      return { success: true };
    }

    setIsDeleting(true)
    try {
      await apiClient.document.deleteDocument(uploadedDocument.id)
      setUploadedDocument(undefined)
      toast.success(String(t('chat.documentRemoved')))
      return { success: true };
    } catch (error: any) {
      const errorMessage = error?.message || t('chat.documentRemoveFailed');
      setLastError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsDeleting(false)
    }
  }, [uploadedDocument, t])

  const clearDocument = useCallback(() => {
    setUploadedDocument(undefined)
  }, [])

  const clearError = useCallback(() => {
    setLastError(null);
  }, [])

  return {
    uploadedDocument,
    isUploading,
    isDeleting,
    lastError,
    handleFileUpload,
    handleDocumentDelete,
    clearDocument,
    clearError,
  }
}
